// ══════════════════════════════════════════════════════════
//  #FLEET_FUEL_CARDS_V1 (2026-10-13) — fuel cards by unit (Voyager cards + Impac fills), on Fleet → Fuel.
//
//  Bill: "Each card number i was thinking to get into unit number in Master so we have them tracked" and
//  "lets do what we can now". Two records, both only written from this screen (or seeded once by Claude
//  from the 10/13 Voyager reports + the Impac Sept / week-of-10/5 exports):
//    fleet-fuel-cards  { v, loadedAt, by, source, cards:[{ card, status, vid, vin, desc, unit, how, idUnit }] }
//                       card = Voyager Card ID (700xxx — NOT the card number); vid = Voyager Vehicle ID;
//                       unit resolved against the Equipment Master by VIN first (the Master is the VIN
//                       source of truth), then by Vehicle ID (DUC unit # without the dash).
//    fleet-fuel-tx     { v, cols, rows:[[…]], files:[{ name, from, to, added, at, by }] } — one row per Impac
//                       fill. Re-loading an export never doubles a fill (key = card|date|time|$|product).
//  The Master shows the card on each unit through fuelCardsByUnit() — the card list stays its own record,
//  refreshed from Voyager, so a card moved to another truck in Voyager shows up here instead of being typed twice.
// ══════════════════════════════════════════════════════════
import { useEffect, useMemo, useState, Fragment } from "react";
import { unitKey } from "./unitValid";

export const FUEL_CARDS_KEY = 'fleet-fuel-cards';
export const FUEL_TX_KEY = 'fleet-fuel-tx';
const TX_COLS = ['k', 'date', 'time', 'card', 'vid', 'vdesc', 'driver', 'product', 'gal', 'amt', 'odo', 'ppg', 'merchant', 'city', 'st'];
const FEE_CARD = '700000';

const vinLoose = (v) => String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/I/g, '1').replace(/[OQ]/g, '0');
const pad2 = (n) => String(n).padStart(2, '0');
const isoOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const mdy = (iso) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || '')); return m ? `${m[2]}/${m[3]}/${m[1].slice(2)}` : ''; };
const money = (n) => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const num1 = (n) => (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 1 });
// Pump odometers drivers key in when they don't know the real one: 0–499, 9999, 1111, 123, 123456 …
export const junkOdo = (o) => {
  const s = String(o == null ? '' : o).replace(/\D/g, '');
  if (!s) return true;
  const n = Number(s);
  return n < 500 || /^(\d)\1{2,}$/.test(s) || '1234567890'.startsWith(s) || /^12345?6?0*$/.test(s);
};

const expand = (blob) => {
  if (!blob || !Array.isArray(blob.rows)) return [];
  const c = Array.isArray(blob.cols) ? blob.cols : TX_COLS;
  return blob.rows.filter(Array.isArray).map(r => { const o = {}; c.forEach((k, i) => { o[k] = r[i]; }); return o; });
};
const pack = (list) => list.map(o => TX_COLS.map(k => (o[k] == null ? '' : o[k])));

// Master lookups — VIN first, then Voyager Vehicle ID = unit # without the dash (rentals: R + that).
export const masterIndex = (master) => {
  const byVin = {}, byId = {};
  (master || []).forEach((r) => {
    if (!r || !r.unit) return;
    const k = vinLoose(r.vin); if (k.length >= 11 && !byVin[k]) byVin[k] = r.unit;
    const id = unitKey(r.unit); if (id) { const plain = id.replace(/^R/, ''); if (!byId[plain] || !/^R/.test(id)) byId[plain] = r.unit; }
  });
  return { byVin, byId };
};
export const resolveCardUnit = (idx, vid, vin) => {
  const vu = idx.byVin[vinLoose(vin)] || '';
  const iu = idx.byId[String(vid || '').replace(/\D/g, '')] || '';
  return { unit: vu || iu, how: vu ? 'vin' : (iu ? 'id' : ''), idUnit: iu };
};

// unitKey → [cards] for the Equipment Master column. Active + pending cards only.
export const fuelCardsByUnit = (blob) => {
  const m = {};
  ((blob && Array.isArray(blob.cards)) ? blob.cards : []).forEach((c) => {
    if (!c || !c.unit || c.card === FEE_CARD) return;
    if (!/ACTIVE|PENDING/i.test(String(c.status || ''))) return;
    const k = unitKey(c.unit); (m[k] = m[k] || []).push(c.card);
  });
  return m;
};
export function useFuelCardIndex(sg) {
  const [idx, setIdx] = useState({});
  useEffect(() => { let dead = false; (async () => { try { const b = await sg(FUEL_CARDS_KEY); if (!dead) setIdx(fuelCardsByUnit(b)); } catch { /* column just shows — */ } })(); return () => { dead = true; }; }, [sg]);
  return idx;
}

// ── file readers ──
const parseCsv = (text) => {
  const rows = []; let row = [], cell = '', q = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (q) { if (ch === '"') { if (text[i + 1] === '"') { cell += '"'; i++; } else q = false; } else cell += ch; }
    else if (ch === '"') q = true;
    else if (ch === ',') { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') { if (ch === '\r' && text[i + 1] === '\n') i++; row.push(cell); rows.push(row); row = []; cell = ''; }
    else cell += ch;
  }
  if (cell || row.length) { row.push(cell); rows.push(row); }
  const head = (rows.shift() || []).map(h => h.trim());
  return rows.filter(r => r.some(x => String(x).trim())).map(r => { const o = {}; head.forEach((h, i) => { o[h] = (r[i] || '').trim(); }); return o; });
};
const loadSheetJS = () => new Promise((resolve, reject) => {
  if (window.XLSX) { resolve(window.XLSX); return; }
  const s = document.createElement('script');
  s.src = 'https://cdn.sheetjs.com/xlsx-0.20.2/package/dist/xlsx.full.min.js';
  s.onload = () => resolve(window.XLSX); s.onerror = () => reject(new Error('could not load the spreadsheet reader'));
  document.head.appendChild(s);
});
const cellDate = (v, XLSX) => {
  if (v instanceof Date && !isNaN(v)) return isoOf(v);
  if (typeof v === 'number' && XLSX) { const p = XLSX.SSF.parse_date_code(v); return p ? `${p.y}-${pad2(p.m)}-${pad2(p.d)}` : ''; }
  const d = new Date(String(v || '')); return isNaN(d) ? '' : isoOf(d);
};
const cellTime = (v) => {
  if (v instanceof Date && !isNaN(v)) return `${pad2(v.getHours())}:${pad2(v.getMinutes())}`;
  if (typeof v === 'number') { const m = Math.round((v % 1) * 1440); return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`; }
  const m = /(\d{1,2}):(\d{2})/.exec(String(v || '')); return m ? `${pad2(m[1])}:${m[2]}` : '';
};
const impacRow = (r, XLSX) => {
  const g = (...names) => { for (const n of names) { if (r[n] !== undefined && r[n] !== '') return r[n]; } return ''; };
  const card = String(g('Card ID')).trim();
  if (!card) return null;
  const date = cellDate(g('Transaction Occurred Date', 'Transaction Date'), XLSX);
  const time = cellTime(g('Transaction Time'));
  const amt = Math.round((Number(g('Transaction Line Amount')) || 0) * 100) / 100;
  const product = String(g('Product Description')).trim();
  return {
    k: `${card}|${date}|${time}|${amt}|${product}`, date, time, card,
    vid: String(g('Vehicle ID')).trim(), vdesc: String(g('Vehicle Description')).trim(),
    driver: `${String(g('Driver First Name')).trim()} ${String(g('Driver Last Name')).trim()}`.trim(),
    product, gal: Number(g('Units')) || 0, amt, odo: String(g('Actual Odometer')).trim(), ppg: Number(g('PPG')) || 0,
    merchant: String(g('Merchant Name')).trim(), city: String(g('Merchant City')).trim(), st: String(g('Merchant State')).trim(),
  };
};

const RANGES = [['30', 'Last 30 days'], ['60', 'Last 60 days'], ['all', 'All loaded']];

export default function FuelCardsPanel({ sg, ss, F, canEdit, master, upn, onCardsChanged }) {
  const [cardsBlob, setCardsBlob] = useState(null);
  const [txBlob, setTxBlob] = useState(null);
  const [range, setRange] = useState('all');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState('');
  const [sort, setSort] = useState({ k: 'amt', dir: -1 });
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  const reload = async () => {
    try { setCardsBlob((await sg(FUEL_CARDS_KEY)) || { cards: [] }); } catch { setCardsBlob({ cards: [] }); }
    try { setTxBlob((await sg(FUEL_TX_KEY)) || { rows: [] }); } catch { setTxBlob({ rows: [] }); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);

  const tx = useMemo(() => expand(txBlob), [txBlob]);
  const cards = useMemo(() => ((cardsBlob && Array.isArray(cardsBlob.cards)) ? cardsBlob.cards : []), [cardsBlob]);
  const lastDate = useMemo(() => tx.reduce((m, t) => (t.date > m ? t.date : m), ''), [tx]);
  const firstDate = useMemo(() => tx.reduce((m, t) => (!m || (t.date && t.date < m) ? t.date : m), ''), [tx]);
  const since = useMemo(() => {
    if (range === 'all') return '';
    const d = new Date(); d.setDate(d.getDate() - Number(range)); return isoOf(d);
  }, [range]);

  const rows = useMemo(() => {
    const byCard = {};
    cards.forEach(c => { if (c.card !== FEE_CARD && /ACTIVE|PENDING/i.test(String(c.status || ''))) byCard[c.card] = { ...c, fills: [], };});
    tx.forEach(t => {
      if (!t.card || t.card === FEE_CARD) return;
      if (since && t.date < since) return;
      if (!byCard[t.card]) { const c = cards.find(x => x.card === t.card); byCard[t.card] = { ...(c || { card: t.card, vid: t.vid, desc: t.vdesc, unit: '', status: c ? c.status : 'not on card list' }), fills: [] }; }
      byCard[t.card].fills.push(t);
    });
    return Object.values(byCard).map(c => {
      const f = c.fills;
      const gal = f.reduce((s, t) => s + (Number(t.gal) || 0), 0);
      const amt = f.reduce((s, t) => s + (Number(t.amt) || 0), 0);
      const last = f.reduce((m, t) => (t.date > m ? t.date : m), '');
      const drivers = [...new Set(f.map(t => t.driver).filter(Boolean))];
      const junk = f.filter(t => Number(t.gal) > 0 && junkOdo(t.odo)).length;
      const notes = [];
      if (!f.length) notes.push('No fills in range');
      if (!c.unit) notes.push('Not on Master');
      if (c.unit && c.idUnit && c.idUnit !== c.unit) notes.push(`Voyager ID ${c.vid} is ${c.idUnit} — matched to ${c.unit} by VIN`);
      else if (c.unit && c.how === 'vin' && unitKey(c.unit).replace(/^R/, '') !== String(c.vid || '')) notes.push(`Voyager ID ${c.vid} — matched by VIN`);
      if (c.unit && c.how === 'id') notes.push(c.vin ? `Voyager VIN ${c.vin} ≠ Master` : 'No VIN in Voyager');
      if (junk) notes.push(`Fake odometer ×${junk}`);
      if (!/ACTIVE|PENDING/i.test(String(c.status || ''))) notes.push(`Card ${String(c.status || '').toLowerCase()}`);
      return { ...c, n: f.length, gal, amt, last, drivers, junk, notes };
    });
  }, [cards, tx, since]);

  const shown = useMemo(() => {
    const s = q.trim().toUpperCase();
    const list = s ? rows.filter(r => [r.card, r.unit, r.vid, r.desc, ...(r.drivers || [])].join(' ').toUpperCase().includes(s)) : rows;
    const k = sort.k, d = sort.dir;
    const val = (r) => (k === 'unit' ? (r.unit || '~') : k === 'card' ? r.card : k === 'last' ? (r.last || '') : k === 'notes' ? r.notes.length : r[k]);
    return [...list].sort((a, b) => { const x = val(a), y = val(b); return (x > y ? 1 : x < y ? -1 : 0) * d || String(a.card).localeCompare(String(b.card)); });
  }, [rows, q, sort]);

  const tot = useMemo(() => rows.reduce((s, r) => ({ n: s.n + r.n, gal: s.gal + r.gal, amt: s.amt + r.amt, idle: s.idle + (r.n ? 0 : 1), junk: s.junk + r.junk }), { n: 0, gal: 0, amt: 0, idle: 0, junk: 0 }), [rows]);
  const activeCount = cards.filter(c => c.card !== FEE_CARD && /ACTIVE/i.test(String(c.status || ''))).length;

  // ── loads ──
  const loadImpac = async (files) => {
    if (!files || !files.length) return;
    setBusy('Reading the Impac export…'); setMsg('');
    try {
      const XLSX = await loadSheetJS();
      let fresh = null; try { fresh = await sg(FUEL_TX_KEY); } catch { fresh = null; }
      const cur = expand(fresh); const have = new Set(cur.map(t => t.k));
      const fileLog = (fresh && Array.isArray(fresh.files)) ? fresh.files : [];
      let added = 0; const notes = [];
      for (const file of files) {
        const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval: '', raw: true });
        if (!raw.length || !('Card ID' in raw[0])) { notes.push(`${file.name}: no "Card ID" column — not an Impac transaction export`); continue; }
        const parsed = raw.map(r => impacRow(r, XLSX)).filter(Boolean);
        let n = 0; parsed.forEach(t => { if (!have.has(t.k)) { have.add(t.k); cur.push(t); n++; } });
        const ds = parsed.map(t => t.date).filter(Boolean).sort();
        fileLog.push({ name: file.name, from: ds[0] || '', to: ds[ds.length - 1] || '', rows: parsed.length, added: n, at: new Date().toISOString(), by: upn || '' });
        added += n; notes.push(`${file.name}: ${parsed.length} fills, ${n} new`);
      }
      cur.sort((a, b) => (a.date + a.time < b.date + b.time ? -1 : 1));
      if (added) await ss(FUEL_TX_KEY, { v: 1, cols: TX_COLS, rows: pack(cur), files: fileLog.slice(-200) });
      setMsg(notes.join(' · ') + (added ? '' : ' — nothing new to save'));
      await reload();
    } catch (e) { setMsg('Could not load that file: ' + (e.message || e)); }
    setBusy('');
  };
  const loadVoyager = async (files) => {
    if (!files || !files.length) return;
    setBusy('Reading the Voyager reports…'); setMsg('');
    try {
      let cardRows = null, vehRows = null;
      for (const file of files) {
        const rs = parseCsv(await file.text());
        if (rs.length && 'Card Tie To' in rs[0]) cardRows = rs;
        else if (rs.length && 'VIN' in rs[0] && 'Vehicle ID' in rs[0]) vehRows = rs;
      }
      if (!cardRows || !vehRows) { setMsg('Need both Voyager CSVs: the Card Report and the Vehicle Report (Reports → Inventory Reports, Output Format = CSV, status = All).'); setBusy(''); return; }
      const veh = {}; vehRows.forEach(v => { veh[v['Vehicle ID']] = v; });
      const idx = masterIndex(master);
      const list = cardRows.map(c => {
        const v = veh[c['Card Tie To']] || {};
        const r = resolveCardUnit(idx, c['Card Tie To'], v['VIN']);
        return { card: c['Card ID'], status: c['Card Status'], vid: c['Card Tie To'] || '', vin: v['VIN'] || '', desc: v['Vehicle Description'] || '', ...r };
      }).filter(c => c.card);
      const live = list.filter(c => /ACTIVE|PENDING/i.test(c.status) && c.card !== FEE_CARD);
      await ss(FUEL_CARDS_KEY, { v: 1, loadedAt: new Date().toISOString(), by: upn || '', source: files.map(f => f.name).join(', '), cards: list });
      setMsg(`${list.length} cards read — ${live.length} active/pending, ${live.filter(c => c.unit).length} matched to a Master unit.`);
      await reload(); if (onCardsChanged) onCardsChanged();
    } catch (e) { setMsg('Could not read those files: ' + (e.message || e)); }
    setBusy('');
  };

  const th = { padding: '7px 9px', fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: .4, color: F.muted, textAlign: 'left', position: 'sticky', top: 0, background: F.card, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };
  const td = { padding: '5px 9px', fontSize: 12, color: F.text, verticalAlign: 'top', borderTop: `1px solid ${F.line}` };
  const btn = { background: 'transparent', border: `1px solid ${F.line}`, color: F.text, borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
  const head = (k, l, right) => (
    <th key={k} onClick={() => setSort(s => (s.k === k ? { k, dir: -s.dir } : { k, dir: k === 'unit' || k === 'card' ? 1 : -1 }))} style={{ ...th, textAlign: right ? 'right' : 'left', color: sort.k === k ? F.accent : F.muted }}>
      {l}{sort.k === k ? (sort.dir > 0 ? ' ▲' : ' ▼') : ''}</th>
  );
  const tile = (label, value, color) => (
    <div style={{ flex: '1 1 120px', background: F.cardHi || F.bg, border: `1px solid ${F.line}`, borderRadius: 10, padding: '8px 12px' }}>
      <div style={{ fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: .4, color: F.muted }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 900, color: color || F.text }}>{value}</div>
    </div>
  );

  return (
    <div style={{ background: F.card, border: `1px solid ${F.line}`, borderRadius: 12, padding: '12px 14px', marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 900, color: F.accent }}>Fuel Cards · by unit</div>
        <div style={{ fontSize: 11.5, color: F.muted }}>
          Cards from Voyager {cardsBlob && cardsBlob.loadedAt ? mdy(cardsBlob.loadedAt.slice(0, 10)) : '— not loaded'}
          {' · '}Impac fills {firstDate ? `${mdy(firstDate)} – ${mdy(lastDate)}` : '— not loaded'}
        </div>
        <div style={{ flex: 1 }} />
        {canEdit && (
          <Fragment>
            <label style={{ ...btn, color: F.accent, borderColor: `${F.accent}66` }} title="Impac weekly / monthly Transaction Report (.xlsx). Loading the same file twice never doubles a fill.">
              Load Impac export
              <input type="file" accept=".xlsx,.xls" multiple style={{ display: 'none' }} onChange={e => { const f = [...e.target.files]; e.target.value = ''; loadImpac(f); }} />
            </label>
            <label style={btn} title="Voyager → Reports → Card Report and Vehicle Report, Output Format = CSV, status = All. Pick both files together.">
              Load Voyager card list
              <input type="file" accept=".csv" multiple style={{ display: 'none' }} onChange={e => { const f = [...e.target.files]; e.target.value = ''; loadVoyager(f); }} />
            </label>
          </Fragment>
        )}
      </div>
      {(busy || msg) && <div style={{ fontSize: 12, color: busy ? F.gold : F.muted, marginBottom: 8 }}>{busy || msg}</div>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        {tile('Active cards', activeCount)}
        {tile('Fills', tot.n.toLocaleString())}
        {tile('Gallons', num1(tot.gal))}
        {tile('Spend', money(tot.amt), F.gold)}
        {tile('Cards with no fills', tot.idle, tot.idle ? F.amber : F.text)}
        {tile('Fake odometers', tot.junk, tot.junk ? F.red : F.text)}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        {RANGES.map(([k, l]) => (
          <button key={k} onClick={() => setRange(k)} style={{ ...btn, background: range === k ? F.accent : 'transparent', color: range === k ? '#04121C' : F.text, borderColor: range === k ? F.accent : F.line }}>{l}</button>
        ))}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Find card, unit, driver…" style={{ ...btn, fontWeight: 500, minWidth: 200, cursor: 'text' }} />
        <span style={{ fontSize: 11, color: F.muted }}>Click a row for its fills.</span>
      </div>

      <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 340px)', border: `1px solid ${F.line}`, borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            {head('card', 'Card')}{head('unit', 'Unit')}<th style={{ ...th, cursor: 'default' }}>Vehicle</th>
            {head('n', 'Fills', true)}{head('gal', 'Gallons', true)}{head('amt', 'Spend', true)}{head('last', 'Last fill')}
            <th style={{ ...th, cursor: 'default' }}>Drivers</th>{head('notes', 'Check')}
          </tr></thead>
          <tbody>
            {shown.map(r => (
              <Fragment key={r.card}>
                <tr onClick={() => setOpen(o => (o === r.card ? '' : r.card))} style={{ cursor: 'pointer', background: open === r.card ? `${F.accent}14` : (!r.unit ? `${F.red}10` : 'transparent') }}>
                  <td style={{ ...td, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{r.card}</td>
                  <td style={{ ...td, fontWeight: 800, color: r.unit ? F.text : F.muted }}>{r.unit || '—'}</td>
                  <td style={{ ...td, color: F.muted }}>{r.desc || '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{r.n || <span style={{ color: F.amber }}>0</span>}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{r.n ? num1(r.gal) : ''}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{r.n ? money(r.amt) : ''}</td>
                  <td style={{ ...td, color: F.muted, whiteSpace: 'nowrap' }}>{mdy(r.last) || '—'}</td>
                  <td style={{ ...td, color: F.muted, fontSize: 11.5 }}>{(r.drivers || []).slice(0, 3).join(', ')}{(r.drivers || []).length > 3 ? ` +${r.drivers.length - 3}` : ''}</td>
                  <td style={{ ...td, fontSize: 11.5, color: r.notes.length ? F.amber : F.green }}>{r.notes.length ? r.notes.join(' · ') : '✓'}</td>
                </tr>
                {open === r.card && (
                  <tr><td colSpan={9} style={{ ...td, background: F.bg, padding: '6px 12px 10px' }}>
                    {r.fills.length ? (
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr>{['Date', 'Time', 'Driver', 'Product', 'Gal', '$', 'Odometer', 'Where'].map(h => <th key={h} style={{ ...th, position: 'static', background: 'transparent', cursor: 'default' }}>{h}</th>)}</tr></thead>
                        <tbody>{[...r.fills].sort((a, b) => (a.date + a.time < b.date + b.time ? 1 : -1)).map(t => (
                          <tr key={t.k}>
                            <td style={td}>{mdy(t.date)}</td><td style={{ ...td, color: F.muted }}>{t.time}</td><td style={td}>{t.driver || '—'}</td>
                            <td style={{ ...td, color: F.muted }}>{t.product}</td><td style={{ ...td, textAlign: 'right' }}>{num1(t.gal)}</td>
                            <td style={{ ...td, textAlign: 'right' }}>{money(t.amt)}</td>
                            <td style={{ ...td, textAlign: 'right', color: Number(t.gal) > 0 && junkOdo(t.odo) ? F.red : F.text }}>{t.odo || '—'}</td>
                            <td style={{ ...td, color: F.muted }}>{[t.merchant, t.city, t.st].filter(Boolean).join(', ')}</td>
                          </tr>))}</tbody>
                      </table>
                    ) : <div style={{ fontSize: 12, color: F.muted }}>No fills on this card in the loaded Impac data{range !== 'all' ? ' for this range' : ''}.{r.vin ? ` Voyager VIN ${r.vin}.` : ' No VIN in Voyager.'}</div>}
                  </td></tr>
                )}
              </Fragment>
            ))}
            {!shown.length && <tr><td colSpan={9} style={{ ...td, color: F.muted, textAlign: 'center', padding: 18 }}>{cards.length || tx.length ? 'Nothing matches.' : 'No card list or Impac fills loaded yet.'}</td></tr>}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: F.muted, marginTop: 8 }}>
        Card = Voyager Card ID, not the card number. Units match the Equipment Master by VIN first, then by Voyager Vehicle ID (unit # without the dash).
        Yard-tank fills are not in this list — local trucks that fill at the yard will show few or no card fills.
      </div>
    </div>
  );
}
